<template>
  <div>
    <!-- Trigger button to open modal -->
    <div
      class="is-rounded"
      :class="{
        'opacity-50': disabled,
        'is-clickable': !disabled,
      }"
      @click="openMapModal"
      :aria-disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      style="margin-top: 10px;margin-bottom: 20px;border: 1px solid rgb(219 219 219);border-radius: 10px;"
    >
      <img
        :src="
          `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
            searchLocation.name || ''
          )}&zoom=10&size=300x150&maptype=roadmap
&markers=color:red%7C${searchLocation.latitude},${searchLocation.longitude}
&key=${this.$appConfig.googleKey}`
        "
        :alt="`Map of ${searchLocation.name || ''}`"
        :class="{ 'grayscale': disabled }"
        style="width: 100%; border-radius: 10px;"
      />
      <div
        class="has-background-white p-4 is-rounded"
        :class="{
          'is-clickable': !disabled,
        }"
        style="border-radius: 10px;"
      >
        <p
          class="has-text-centered is-size-6 mx-auto"
          :class="{
            'has-text-grey-light': disabled,
            'has-text-info': !disabled,
          }"
        >
          View on a map
        </p>
      </div>
    </div>

    <!-- Buefy Modal -->
    <b-modal
      full-screen
      v-model="isMapModalActive"
      scroll="clip"
      :can-cancel="['escape']"
      @close="onModalClose"
    >
      <!-- Full-width container for the map -->
      <div class="modal-container">
        <!-- Top panel with back button, search location and close button -->
        <div class="top-panel">
          <div class="back-button-container">
            <button
              class="back-button"
              @click="closeMapModal"
              aria-label="Go back to search results"
            >
              <i class="fas fa-arrow-left"></i>
              <span>Back to Results</span>
            </button>
          </div>
          <div class="back-button-container">
            <button
              class="back-button"
              @click="isFilterModalActive = true"
              aria-label="Filter search results"
            >
              <b-icon icon="tune" type="is-default" />
              Filters
              <span v-if="modifiedFiltersCount > 0" class="filter-count-badge">
                {{ modifiedFiltersCount }}
              </span>
            </button>
          </div>
          <div class="search-location-info">
            <div class="search-location-label">
              Showing hotels near:
            </div>
            <h3 class="search-location-title">
              <i class="fas fa-map-marker-alt location-icon"></i>
              {{ searchLocation.name || "" }}
            </h3>
          </div>
          <button
            type="button"
            class="delete is-large is-primary close-button"
            @click="closeMapModal"
            aria-label="close"
          ></button>
        </div>

        <!-- Map Container - Full screen minus top panel -->
        <div id="hotel-map" ref="mapContainer" class="full-screen-map"></div>

        <!-- Search This Area Button -->
        <div
          v-if="showSearchAreaButton && !isSearching"
          class="search-area-button-container"
        >
          <button
            class="search-area-button"
            @click="searchThisArea"
            :disabled="isSearching"
          >
            <i class="fas fa-search"></i>
            <span>Search This Area</span>
          </button>
        </div>

        <!-- Loading overlay for search -->
        <div v-if="isSearching" class="search-loading-overlay">
          <div class="search-loading-card">
            <div class="loading-spinner"></div>
            <p class="has-text-white">Searching for hotels...</p>
          </div>
        </div>

        <!-- Progressive loading indicator -->
        <div v-if="isLoadingMarkers" class="marker-loading-overlay">
          <div class="marker-loading-card">
            <div class="loading-spinner"></div>
            <p class="has-text-white">
              Loading {{ loadedMarkersCount }} of
              {{ totalMarkersToLoad }} hotels...
            </p>
          </div>
        </div>

        <!-- Hotel count info overlay -->
        <div class="map-info-overlay">
          <div class="info-card">
            <p class="is-size-7 has-text-white">
              Showing {{ hotelsInViewport }} of {{ totalHotels.length }} hotels
              <span v-if="filteredResults.length < totalHotels.length">
                ({{ filteredResults.length }}
                filtered)
              </span>
            </p>
          </div>
        </div>

        <!-- Optimized marker management - no Vue components needed -->
        <!-- Markers are now managed directly in JavaScript for better performance -->

        <FilterModal
          :is-active="isFilterModalActive"
          :show-map-launcher="false"
          @update:isActive="isFilterModalActive = $event"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import GoogleMapsService from "@/services/GoogleMapsService.js"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import FilterModal from "@/components/Filters/FilterModal.vue"

export default {
  name: "HotelMapModal",
  components: {
    FilterModal,
  },
  props: {
    searchLocation: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value &&
          typeof value.latitude === "number" &&
          typeof value.longitude === "number"
        )
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isMapModalActive: false,
      isFilterModalActive: false,
      map: null,
      markerClusterer: null,
      hotelsInViewport: 0,
      viewportUpdateTimeout: null,
      // Search by map functionality
      showSearchAreaButton: false,
      isSearching: false,
      initialMapBounds: null,
      initialBoundsSet: false,
      mapMoveTimeout: null,
      searchAreaButtonTimeout: null,
      // Search center marker - large distinctive marker for search center
      searchCenterMarker: null,
      // Location name cache to avoid repeated geocoding
      locationNameCache: new Map(),
      // Optimized marker management
      activeMarkers: new Map(), // Map of hotel.uniqueKey -> marker instance
      markerPool: [], // Pool of reusable marker objects
      iconCache: new Map(), // Cache for generated SVG icons
      infoWindow: null, // Single reusable InfoWindow
      infoWindowContentCache: new Map(), // Cache for InfoWindow content
      infoWindowTemplate: null, // Pre-compiled template
      currentViewportBounds: null,
      markerUpdateDebounceTimeout: null,
      // Clustering optimization
      clusterCache: new Map(), // Cache for cluster configurations
      lastClusterZoom: null,
      clusterUpdateTimeout: null,
      // Debouncing for map events
      idleDebounceTimeout: null,
      boundsChangedTimeout: null,
      viewportCalculationTimeout: null,
      // Progressive loading
      isLoadingMarkers: false,
      loadedMarkersCount: 0,
      totalMarkersToLoad: 0,
      progressiveLoadingQueue: [],
      progressiveLoadingTimeout: null,
      // Performance settings
      VIEWPORT_BUFFER: 0.2, // 20% buffer around viewport for marker loading
      MAX_VISIBLE_MARKERS: 200, // Maximum markers to show at once
      MARKER_UPDATE_DEBOUNCE: 150, // Debounce time for marker updates
      MARKER_POOL_SIZE: 50, // Maximum markers to keep in pool
      ICON_CACHE_SIZE: 100, // Maximum icons to cache
      CLUSTER_UPDATE_DEBOUNCE: 200, // Debounce time for cluster updates
      IDLE_DEBOUNCE: 300, // Debounce time for idle events
      BOUNDS_CHANGED_DEBOUNCE: 200, // Debounce time for bounds changed events
      VIEWPORT_CALC_DEBOUNCE: 100, // Debounce time for viewport calculations
      INFOWINDOW_CACHE_SIZE: 50, // Maximum InfoWindow content to cache
      PROGRESSIVE_BATCH_SIZE: 20, // Number of markers to load per batch
      PROGRESSIVE_BATCH_DELAY: 50, // Delay between batches (ms)
      PROGRESSIVE_LOADING_THRESHOLD: 50, // Start progressive loading if more than this many hotels
      // Memory management
      MEMORY_CLEANUP_INTERVAL: 30000, // Clean up unused resources every 30 seconds
      memoryCleanupInterval: null,
    }
  },
  computed: {
    ...mapState({
      totalHotels: (state) => state.hotelSearch.searchResults,
      resultsLoading: (state) => state.hotelSearch.resultsLoading,
    }),
    ...mapGetters({
      filteredResults: "hotelSearch/filteredResults",
      modifiedFiltersCount: "hotelSearch/modifiedFiltersCount",
    }),
    hotels() {
      return this.filteredResults
    },
  },

  watch: {
    isMapModalActive(newValue, oldValue) {
      if (!newValue && oldValue) {
        // Modal is closing - ensure cleanup
        this.cleanup()
      }
    },
    hotels: {
      handler(newHotels, oldHotels) {
        if (this.map && this.isMapModalActive) {
          // Check if this is a completely new search (different hotel set)
          const isNewSearch = this.isNewSearchResults(newHotels, oldHotels)

          if (isNewSearch) {
            // Clear all existing markers for new search
            this.clearAllMarkers()
          }

          // Use optimized marker update system
          this.debouncedUpdateMarkers()
        }
      },
      deep: true,
    },

    searchLocation: {
      handler(newLocation) {
        if (this.map && this.isMapModalActive && newLocation) {
          // Update search center marker when search location changes
          this.updateSearchCenterMarker(newLocation)
        }
      },
      deep: true,
    },
  },

  methods: {
    openMapModal() {
      if (this.disabled) return

      this.isMapModalActive = true

      // Push a new history state when opening modal
      if (window.history.pushState) {
        window.history.pushState(
          { modalOpen: true },
          "",
          window.location.href + "#map"
        )

        // Listen for popstate (back button)
        window.addEventListener("popstate", this.handleBackButton)
      }

      // Log the event
      this.$app.logEvent({
        event_type: "Hotel Map Opened",
      })

      // Wait for modal to render before initializing map
      this.$nextTick(() => {
        // Add a small delay to ensure DOM is fully ready
        setTimeout(() => {
          this.initializeMap()
        }, 100)
      })
    },

    closeMapModal() {
      this.isMapModalActive = false
      this.cleanup()

      // Remove the history state if it was added by the modal
      if (window.history.state && window.history.state.modalOpen) {
        window.history.back()
      }

      // Remove event listener
      window.removeEventListener("popstate", this.handleBackButton)
    },

    onModalClose() {
      this.cleanup()

      // Remove event listener when modal is closed by other means (ESC, click outside)
      window.removeEventListener("popstate", this.handleBackButton)
    },

    handleBackButton(event) {
      if (this.isMapModalActive) {
        // Prevent the default back navigation
        event.preventDefault()

        // Close the modal instead
        this.isMapModalActive = false
        this.cleanup()

        // Remove the event listener
        window.removeEventListener("popstate", this.handleBackButton)
      }
    },

    async initializeMap() {
      try {
        // Load Google Maps API if not already loaded
        const google = await GoogleMapsService.getGoogleMaps()

        // Initialize map centered on search location
        const mapOptions = {
          //mapId: "DEMO_MAP_ID",
          center: {
            lat: this.searchLocation.latitude,
            lng: this.searchLocation.longitude,
          },
          zoom: 16,
          mapTypeId: "roadmap",
          styles: [
            {
              featureType: "poi.business",
              stylers: [{ visibility: "off" }],
            },
          ],
          mapTypeControl: false,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_CENTER,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          cameraControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }

        this.map = new google.maps.Map(this.$refs.mapContainer, mapOptions)

        // Set up map event listeners for viewport changes
        this.setupMapEventListeners()

        // Initialize optimized marker system
        this.$nextTick(() => {
          // Initialize single reusable InfoWindow
          this.initializeInfoWindow()

          // Add search center marker
          this.addSearchCenterMarker()

          // Initial marker setup
          this.updateMarkersForViewport()
          this.setupMarkerClustering()
          this.fitMapBounds()

          // Initial count of hotels in viewport
          this.calcHotelsInViewport()

          // Start memory management
          this.startMemoryManagement()
        })
      } catch (error) {
        console.error("Failed to initialize Google Maps:", error)
      }
    },

    async getLocationNameFromCoordinates(lat, lng) {
      // Create cache key from rounded coordinates (to avoid excessive precision)
      const cacheKey = `${lat.toFixed(3)},${lng.toFixed(3)}`

      // Check cache first
      if (this.locationNameCache.has(cacheKey)) {
        return this.locationNameCache.get(cacheKey)
      }

      try {
        const google = await GoogleMapsService.getGoogleMaps()

        // Use Geocoder for reverse geocoding
        const geocoder = new google.maps.Geocoder()

        const response = await new Promise((resolve, reject) => {
          geocoder.geocode(
            {
              location: { lat, lng },
              // Prefer results that are useful for hotel searches
              /*result_type: [
                'locality',
                'sublocality',
                'neighborhood',
                'administrative_area_level_2',
                'administrative_area_level_1'
              ]*/
            },
            (results, status) => {
              if (status === "OK") {
                resolve(results)
              } else {
                reject(new Error(`Geocoding failed: ${status}`))
              }
            }
          )
        })

        if (response && response.length > 0) {
          // Try to get the most appropriate location name
          const result = response[0]

          // Look for specific address components in order of preference
          const addressComponents = result.address_components
          let locationName = null

          // Priority order: locality (city) > sublocality > neighborhood > admin area
          /*
          const preferredTypes = [
            'locality',
            'sublocality_level_1',
            'sublocality',
            'neighborhood',
            'administrative_area_level_2',
            'administrative_area_level_1'
          ]

          for (const preferredType of preferredTypes) {
            const component = addressComponents.find(comp =>
              comp.types.includes(preferredType)
            )
            if (component) {
              locationName = component.long_name
              break
            }
          }
          */

          // If no preferred component found, use the formatted address but clean it up
          if (!locationName) {
            locationName = result.formatted_address
            // Remove country and postal code for cleaner display
            locationName = locationName.replace(/,\s*\d{5}.*$/, "") // Remove ZIP and after
            locationName = locationName.replace(/,\s*USA?$/, "") // Remove USA/US
            locationName = locationName.replace(/,\s*United States.*$/, "") // Remove United States
          }

          // Limit length for UI display
          if (locationName.length > 50) {
            locationName = locationName.substring(0, 47) + "..."
          }

          const finalName = locationName || `Search Area`
          // Cache the result
          this.locationNameCache.set(cacheKey, finalName)
          return finalName
        }
      } catch (error) {
        console.warn("Failed to get location name from coordinates:", error)
      }

      // Fallback to a generic but user-friendly name
      const fallbackName = `Search Area`
      this.locationNameCache.set(cacheKey, fallbackName)
      return fallbackName
    },

    // Optimized marker management methods
    isNewSearchResults(newHotels, oldHotels) {
      // Check if this represents a completely new search
      if (!oldHotels || oldHotels.length === 0) return true
      if (!newHotels || newHotels.length === 0) return true

      // Compare first few hotel IDs to detect if it's a different result set
      const newIds = newHotels.slice(0, 5).map(h => h.uniqueKey).sort()
      const oldIds = oldHotels.slice(0, 5).map(h => h.uniqueKey).sort()

      return JSON.stringify(newIds) !== JSON.stringify(oldIds)
    },

    clearAllMarkers() {
      console.log('Clearing all markers for new search')

      // Clear all active markers
      for (const marker of this.activeMarkers.values()) {
        this.recycleMarker(marker)
      }
      this.activeMarkers.clear()

      // Clear clustering
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers()
      }

      // Cancel any pending progressive loading
      this.cancelProgressiveLoading()
    },

    debouncedUpdateMarkers() {
      if (this.markerUpdateDebounceTimeout) {
        clearTimeout(this.markerUpdateDebounceTimeout)
      }

      this.markerUpdateDebounceTimeout = setTimeout(() => {
        this.updateMarkersForViewport()
        this.setupMarkerClustering()
        this.calcHotelsInViewport()
      }, this.MARKER_UPDATE_DEBOUNCE)
    },

    updateMarkersForViewport() {
      if (!this.map || !this.hotels.length) return

      const bounds = this.getExpandedViewportBounds()
      if (!bounds) return

      // Get hotels that should be visible
      const visibleHotels = this.getHotelsInBounds(bounds)

      // Limit to maximum visible markers for performance
      const hotelsToShow = visibleHotels.slice(0, this.MAX_VISIBLE_MARKERS)

      // Remove markers that are no longer needed
      this.removeMarkersOutsideBounds(bounds)

      // Add new markers for visible hotels
      this.addMarkersForHotels(hotelsToShow)
    },

    getExpandedViewportBounds() {
      if (!this.map) return null

      const bounds = this.map.getBounds()
      if (!bounds) return null

      // Expand bounds by buffer percentage
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const latDiff = (ne.lat() - sw.lat()) * this.VIEWPORT_BUFFER
      const lngDiff = (ne.lng() - sw.lng()) * this.VIEWPORT_BUFFER

      return new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - latDiff, sw.lng() - lngDiff),
        new window.google.maps.LatLng(ne.lat() + latDiff, ne.lng() + lngDiff)
      )
    },

    getHotelsInBounds(bounds) {
      return this.hotels.filter((hotel) => {
        if (!hotel.coordinates) return false

        const position = new window.google.maps.LatLng(
          hotel.coordinates.latitude,
          hotel.coordinates.longitude
        )

        return bounds.contains(position)
      })
    },

    setupMarkerClustering() {
      // Debounce cluster updates for better performance
      if (this.clusterUpdateTimeout) {
        clearTimeout(this.clusterUpdateTimeout)
      }

      this.clusterUpdateTimeout = setTimeout(() => {
        this.performClusterUpdate()
      }, this.CLUSTER_UPDATE_DEBOUNCE)
    },

    performClusterUpdate() {
      const markers = Array.from(this.activeMarkers.values())

      if (markers.length === 0) {
        if (this.markerClusterer) {
          this.markerClusterer.clearMarkers()
        }
        return
      }

      const currentZoom = this.map.getZoom()
      const cacheKey = `${markers.length}-${currentZoom}`

      // Check if we can reuse existing clusterer configuration
      if (this.markerClusterer && this.lastClusterZoom === currentZoom) {
        // Just update markers without recreating clusterer
        this.markerClusterer.clearMarkers()
        this.markerClusterer.addMarkers(markers)
        return
      }

      console.log(
        `Setting up clustering for ${markers.length} markers at zoom ${currentZoom}`
      )

      // Clear existing clusterer
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers()
        this.markerClusterer = null
      }

      try {
        // Use zoom-adaptive clustering configuration
        const clusterConfig = this.getOptimizedClusterConfig(
          currentZoom,
          markers.length
        )

        this.markerClusterer = new MarkerClusterer({
          map: this.map,
          markers: markers,
          algorithmOptions: clusterConfig.algorithm,
          renderer: {
            render: ({ count, position, markers: clusterMarkers }) => {
              return this.createOptimizedClusterMarker(
                count,
                position,
                clusterMarkers
              )
            },
          },
        })

        this.lastClusterZoom = currentZoom
        console.log("MarkerClusterer initialized successfully")
      } catch (error) {
        console.error("Error initializing MarkerClusterer:", error)
        this.fallbackClustering(markers)
      }
    },

    getOptimizedClusterConfig(zoom, markerCount) {
      // Adaptive clustering based on zoom level and marker count
      let radius, maxZoom, minPoints

      if (zoom <= 10) {
        // Far zoom - aggressive clustering
        radius = 90
        maxZoom = 12
        minPoints = 3
      } else if (zoom <= 14) {
        // Medium zoom - moderate clustering
        radius = 60
        maxZoom = 16
        minPoints = 2
      } else {
        // Close zoom - minimal clustering
        radius = 40
        maxZoom = 18
        minPoints = 2
      }

      // Adjust for marker density
      if (markerCount > 100) {
        radius += 20 // More aggressive clustering for dense areas
        minPoints = Math.max(minPoints, 3)
      }

      return {
        algorithm: {
          radius,
          maxZoom,
          minPoints,
        },
      }
    },

    createOptimizedClusterMarker(count, position, clusterMarkers) {
      // Use cached cluster marker if available
      const cacheKey = `cluster-${count}`

      const clusterText = count > 99 ? "99+" : `${count} properties`
      const baseWidth = 80
      const extraWidth = clusterText.length > 2 ? 10 : 0
      const width = baseWidth + extraWidth
      const height = 36

      const backgroundColor = "#ffffff"
      const textColor = "#a4393a"
      const fontSize = count > 99 ? "10" : "12"

      const svg = `
                  <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <!-- Cluster container background (above the pointer) -->
                    <rect x="2" y="2" width="${width -
                      4}" height="26" rx="13" fill="${backgroundColor}" stroke="${textColor}" stroke-width="2"/>
                    <!-- Cluster text -->
                    <text x="${width /
                      2}" y="19" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">${clusterText}</text>
                  </svg>
                `

      const clusterMarker = new window.google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new window.google.maps.Size(width, height),
          anchor: new window.google.maps.Point(width / 2, height - 2),
        },
        zIndex: 1000, // Higher z-index for clusters
      })

      // Add click event listener
      clusterMarker.addListener("click", () => {
        this.zoomToShowClusterMarkers(clusterMarkers)
      })

      return clusterMarker
    },

    fallbackClustering(markers) {
      try {
        console.log("Using fallback MarkerClusterer API")
        this.markerClusterer = new MarkerClusterer(this.map, markers, {
          gridSize: 60,
          maxZoom: 16,
          minimumClusterSize: 2,
        })

        window.google.maps.event.addListener(
          this.markerClusterer,
          "clusterclick",
          (cluster) => {
            const clusterMarkers = cluster.getMarkers()
            this.zoomToShowClusterMarkers(clusterMarkers)
          }
        )

        console.log("Fallback MarkerClusterer initialized successfully")
      } catch (fallbackError) {
        console.error("Fallback MarkerClusterer also failed:", fallbackError)
      }
    },

    removeMarkersOutsideBounds(bounds) {
      const markersToRemove = []

      for (const [hotelKey, marker] of this.activeMarkers.entries()) {
        const position = marker.getPosition()

        // Remove marker if it's outside bounds OR if the hotel no longer exists in current results
        const hotelStillExists = this.hotels.some(hotel => hotel.uniqueKey === hotelKey)

        if (!bounds.contains(position) || !hotelStillExists) {
          markersToRemove.push(hotelKey)
        }
      }

      markersToRemove.forEach((hotelKey) => {
        const marker = this.activeMarkers.get(hotelKey)
        if (marker) {
          this.recycleMarker(marker)
          this.activeMarkers.delete(hotelKey)
        }
      })
    },

    recycleMarker(marker) {
      // Clean up marker before returning to pool
      marker.setMap(null)

      // Remove event listeners using proper Google Maps API
      if (marker._clickListener) {
        window.google.maps.event.removeListener(marker._clickListener)
        marker._clickListener = null
      }

      // Clear hotel data reference
      marker._hotelData = null

      // Add to pool if not at capacity
      if (this.markerPool.length < this.MARKER_POOL_SIZE) {
        this.markerPool.push(marker)
      }
      // If pool is full, let marker be garbage collected
    },

    addMarkersForHotels(hotels) {
      const newHotels = hotels.filter(
        (hotel) => !this.activeMarkers.has(hotel.uniqueKey)
      )

      if (newHotels.length === 0) return

      // Use progressive loading for large datasets
      if (newHotels.length > this.PROGRESSIVE_LOADING_THRESHOLD) {
        this.startProgressiveLoading(newHotels)
      } else {
        // Load all markers immediately for small datasets
        newHotels.forEach((hotel) => {
          const marker = this.createOptimizedMarker(hotel)
          if (marker) {
            this.activeMarkers.set(hotel.uniqueKey, marker)
          }
        })
      }
    },

    startProgressiveLoading(hotels) {
      // Cancel any existing progressive loading
      this.cancelProgressiveLoading()

      this.isLoadingMarkers = true
      this.loadedMarkersCount = 0
      this.totalMarkersToLoad = hotels.length
      this.progressiveLoadingQueue = [...hotels]

      // Start loading batches
      this.loadNextBatch()
    },

    loadNextBatch() {
      if (this.progressiveLoadingQueue.length === 0) {
        this.finishProgressiveLoading()
        return
      }

      // Load next batch
      const batch = this.progressiveLoadingQueue.splice(
        0,
        this.PROGRESSIVE_BATCH_SIZE
      )

      batch.forEach((hotel) => {
        const marker = this.createOptimizedMarker(hotel)
        if (marker) {
          this.activeMarkers.set(hotel.uniqueKey, marker)
          this.loadedMarkersCount++
        }
      })

      // Schedule next batch
      this.progressiveLoadingTimeout = setTimeout(() => {
        this.loadNextBatch()
      }, this.PROGRESSIVE_BATCH_DELAY)
    },

    finishProgressiveLoading() {
      this.isLoadingMarkers = false
      this.loadedMarkersCount = 0
      this.totalMarkersToLoad = 0
      this.progressiveLoadingQueue = []

      // Update clustering after all markers are loaded
      this.setupMarkerClustering()
    },

    cancelProgressiveLoading() {
      if (this.progressiveLoadingTimeout) {
        clearTimeout(this.progressiveLoadingTimeout)
        this.progressiveLoadingTimeout = null
      }

      this.isLoadingMarkers = false
      this.loadedMarkersCount = 0
      this.totalMarkersToLoad = 0
      this.progressiveLoadingQueue = []
    },

    createOptimizedMarker(hotel) {
      if (!hotel.coordinates) return null

      // Try to reuse marker from pool
      let marker = this.markerPool.pop()

      if (!marker) {
        // Create new marker if pool is empty
        marker = new window.google.maps.Marker({
          clickable: true,
          optimized: true, // Enable marker optimization
          zIndex: 1, // Default z-index for hotel markers
        })
      }

      // Batch update marker properties for better performance
      const position = {
        lat: hotel.coordinates.latitude,
        lng: hotel.coordinates.longitude,
      }

      const currencySymbol = this.$helpers.getCurrencySymbol(hotel.currencyCode)

      const icon = this.getCachedMarkerIcon(hotel.cheapestPrice, currencySymbol)

      // Update all properties at once
      marker.setOptions({
        position: position,
        title: hotel.hotelName,
        icon: icon,
        map: this.map,
      })

      // Add click listener and store the listener reference
      const clickHandler = () => this.onOptimizedMarkerClicked(marker, hotel)
      const listenerRef = marker.addListener("click", clickHandler)

      // Store hotel reference and listener reference for cleanup
      marker._hotelData = hotel
      marker._clickListener = listenerRef

      return marker
    },

    // Search Center Marker Management
    addSearchCenterMarker() {
      if (!this.map || !this.searchLocation) return

      // Remove existing search center marker
      this.removeSearchCenterMarker()

      // Create large, distinctive marker for search center
      this.searchCenterMarker = new window.google.maps.Marker({
        position: {
          lat: this.searchLocation.latitude,
          lng: this.searchLocation.longitude,
        },
        map: this.map,
        title: `Search Center: ${this.searchLocation.name || 'Current Location'}`,
        icon: this.createSearchCenterIcon(),
        zIndex: 2000, // Higher than hotel markers and clusters
        clickable: true,
      })

      // Add click listener to show search center info
      this.searchCenterMarker.addListener('click', () => {
        this.showSearchCenterInfo()
      })

      console.log('Search center marker added:', this.searchLocation.name)
    },

    createSearchCenterIcon() {
      // Create a Google Maps style marker with smooth teardrop shape
      const width = 30
      const height = 35
      const centerX = width / 2
      const centerY = 14 // Center of the circular part
      const radius = 11 // Radius of the circular part

      const svg = `
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
          <!-- Landing pad shadow (ground shadow) -->
          <ellipse cx="${centerX + 1}" cy="${height - 2}" rx="11" ry="3" fill="rgba(0,0,0,0.2)"/>

          <!-- Drop shadow for the teardrop -->
          <path d="M${centerX + 1} ${centerY - radius + 1}
                   A${radius} ${radius} 0 1 1 ${centerX + 1} ${centerY + radius + 1}
                   Q${centerX + 1} ${centerY + radius + 6} ${centerX + 1} ${height - 3}
                   Q${centerX + 1} ${centerY + radius + 6} ${centerX + 1} ${centerY + radius + 1}
                   A${radius} ${radius} 0 1 1 ${centerX + 1} ${centerY - radius + 1} Z"
                fill="rgba(0,0,0,0.3)"/>

          <!-- Main teardrop body with smooth curves -->
          <path d="M${centerX} ${centerY - radius}
                   A${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius}
                   Q${centerX} ${centerY + radius + 5} ${centerX} ${height - 4}
                   Q${centerX} ${centerY + radius + 5} ${centerX} ${centerY + radius}
                   A${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius} Z"
                fill="#ffffff" stroke="#777777" stroke-width="1"/>

          <!-- Inner circle -->
          <circle cx="${centerX}" cy="${centerY}" r="8" fill="#4285f4"/>

          <!-- Optional: Add a subtle highlight for more depth -->
          <ellipse cx="${centerX - 2}" cy="${centerY - 2}" rx="3" ry="2" fill="rgba(255,255,255,0.3)"/>

        </svg>
      `

      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(width, height),
        anchor: new window.google.maps.Point(width/2, height - 3), // Bottom center anchor like Google Maps
      }
    },

    updateSearchCenterMarker(newLocation) {
      if (!newLocation) return

      if (this.searchCenterMarker) {
        // Update existing marker position
        this.searchCenterMarker.setPosition({
          lat: newLocation.latitude,
          lng: newLocation.longitude,
        })
        this.searchCenterMarker.setTitle(`Search Center: ${newLocation.name || 'Current Location'}`)
        console.log('Search center marker updated:', newLocation.name)
      } else {
        // Create new marker if it doesn't exist
        this.addSearchCenterMarker()
      }
    },

    removeSearchCenterMarker() {
      if (this.searchCenterMarker) {
        this.searchCenterMarker.setMap(null)
        this.searchCenterMarker = null
      }
    },

    showSearchCenterInfo() {
      if (!this.infoWindow || !this.searchCenterMarker) return

      const content = `
        <div style="padding: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <h3 style="margin: 0 0 8px 0; color: #4285f4; font-size: 16px; font-weight: 600;">
            🎯 Search Center
          </h3>
          <p style="margin: 0 0 4px 0; font-size: 14px; color: #333;">
            <strong>${this.searchLocation.name || 'Current Location'}</strong>
          </p>
          <p style="margin: 0; font-size: 12px; color: #666;">
            Hotels are searched within ${this.searchLocation.radius ? Math.round(this.searchLocation.radius) : 'a few'} miles of this location
          </p>
        </div>
      `

      this.infoWindow.setContent(content)
      this.infoWindow.open(this.map, this.searchCenterMarker)
    },

    zoomToShowClusterMarkers(clusterMarkers) {
      if (!clusterMarkers || clusterMarkers.length === 0) return

      // Create bounds to encompass all markers in the cluster
      const bounds = new window.google.maps.LatLngBounds()

      clusterMarkers.forEach((marker) => {
        bounds.extend(marker.getPosition())
      })

      // Fit the map to show all markers in the cluster
      this.map.fitBounds(bounds)

      // Add some padding to ensure markers aren't right at the edge
      const padding = {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      }

      // Apply padding and ensure minimum zoom level for visibility
      setTimeout(() => {
        this.map.fitBounds(bounds, padding)

        // Ensure we don't zoom in too much for single markers or very close markers
        const currentZoom = this.map.getZoom()
        const maxZoom = 18 // Maximum zoom level
        const minZoom = 12 // Minimum zoom level for cluster expansion

        if (currentZoom > maxZoom) {
          this.map.setZoom(maxZoom)
        } else if (currentZoom < minZoom && clusterMarkers.length > 1) {
          this.map.setZoom(minZoom)
        }
      }, 100)

      console.log(`Zooming to show ${clusterMarkers.length} individual markers`)
    },

    getCachedMarkerIcon(price, currencySymbol = "USD") {
      // Create cache key based on price
      const priceKey = Math.round(price)

      if (this.iconCache.has(priceKey)) {
        // Move to end for LRU behavior
        const icon = this.iconCache.get(priceKey)
        this.iconCache.delete(priceKey)
        this.iconCache.set(priceKey, icon)
        return icon
      }

      // Generate new icon
      const icon = this.generateMarkerIcon(price, currencySymbol)

      // Implement LRU cache eviction
      if (this.iconCache.size >= this.ICON_CACHE_SIZE) {
        // Remove oldest entry (first key)
        const firstKey = this.iconCache.keys().next().value
        this.iconCache.delete(firstKey)
      }

      this.iconCache.set(priceKey, icon)
      return icon
    },

    generateMarkerIcon(price, currencySymbol) {
      // Color code markers by price range
      const backgroundColor = "#a4393a" // Gant Red
      const textColor = "#ffffff" // White
      const borderColor = "#ffffff"

      // Format price for display
      const displayPrice = price % 1 === 0 ? `${price}` : `${price.toFixed(0)}`

      // Calculate text size based on price length
      const fontSize =
        displayPrice.length <= 4 ? "12" : displayPrice.length <= 5 ? "10" : "9"

      const svg = `
        <svg width="60" height="40" viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 2 L45 2 C53 2 58 7 58 15 C58 23 53 28 45 28 L35 28 L30 38 L25 28 L15 28 C7 28 2 23 2 15 C2 7 7 2 15 2 Z"
                fill="${backgroundColor}"
                stroke="${borderColor}"
                stroke-width="1"
                stroke-linejoin="round"
                stroke-linecap="round"/>
          <text x="30" y="19" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">${currencySymbol}${displayPrice}</text>
        </svg>
      `

      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(60, 40),
        anchor: new window.google.maps.Point(30, 38),
      }
    },

    initializeInfoWindow() {
      if (!window.google) return

      this.infoWindow = new window.google.maps.InfoWindow({
        maxWidth: 700,
        pixelOffset: new window.google.maps.Size(0, -10),
        clickable: true,
      })

      // Pre-compile InfoWindow template for better performance
      this.compileInfoWindowTemplate()
    },

    compileInfoWindowTemplate() {
      // Create a reusable template structure
      this.infoWindowTemplate = {
        wrapper: (content) => `
          <div class="info-window-content" style="max-width: 700px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; border-radius: 12px; overflow: hidden; background: white; padding: 5px;">
            ${content}
          </div>
        `,

        imageSection: (imageUrl, hotelName) => `
          <div class="column image-header" style="position: relative; height: 180px; overflow: hidden;">
            <div class="image-container" style="position: relative; width: 100%; height: 100%;">
              <figure class="image">
                <img src="${imageUrl}" alt="${hotelName}" class="hotel-image" style="width: 100%; height: 100%; object-fit: cover; cursor: pointer;">
              </figure>
            </div>
          </div>
        `,

        detailsSection: (hotelName, distanceText, amenitiesHtml, pricing) => `
          <div class="column hotel-details" style="display: flex; flex-direction: column;">
            <div class="hotel-header content-section">
              <h3 class="hotel-name" style="font-size: 1.375rem; font-weight: 700; line-height: 1.3; margin: 0 0 8px 0; color: #1a1a1a; cursor: pointer;">${hotelName}</h3>
              <div class="location-info" style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px; color: #666; font-size: 0.875rem;">
                <i class="fas fa-map-marker-alt location-icon" style="color: #a4393a; font-size: 12px;"></i>
                <span class="distance-text">${distanceText}</span>
              </div>
            </div>

            <div class="details-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; flex: 1; align-items: start;">
              <div class="left-column" style="display: flex; flex-direction: column; gap: 8px;">
                ${amenitiesHtml}
                <div class="view-button" style="background: #a4393a; color: white; padding: 8px 12px; border-radius: 10px; width: fit-content; cursor: pointer; font-size: 0.875rem; font-weight: bold;">
                  View hotel <i class="fas fa-arrow-right"></i>
                </div>
              </div>

              <div class="right-column" style="display: flex; flex-direction: column; height: 100%; text-align: right;">
                <div class="pricing-section">
                  <div class="price-container" style="display: flex; flex-direction: column; align-items: flex-end;">
                    <div class="price-row" style="display: flex; align-items: baseline; gap: 4px;">
                      <span class="price-label" style="font-size: 0.75rem; color: #666;">approx</span>
                      ${
                        pricing.originalRate
                          ? `<span class="old-price" style="text-decoration: line-through; font-size: 0.875rem; color: #999;">${pricing.currencySymbol}${pricing.originalRate}</span>`
                          : ""
                      }
                      <span class="price-amount" style="font-size: 1.05rem; font-weight: 700; color: #3e8ed0; line-height: 1;">${pricing.currencySymbol}${
                        pricing.dailyRate
                      }</span>
                      <span class="price-period" style="font-size: 0.75rem; color: #666;">nightly</span>
                    </div>
                    <div class="total-price" style="font-weight: 500; color: #10171e; margin-top: 2px; margin-bottom: 2px;">${pricing.currencySymbol}${
                      pricing.total
                    } total</div>
                  </div>
                  <p class="tax-info" style="margin: 0; display: flex; align-items: center; justify-content: flex-end; gap: 3px; font-size: 0.65rem; color: #28a745;">includes taxes & fees</p>
                </div>
              </div>
            </div>
          </div>
        `,
      }
    },

    onOptimizedMarkerClicked(marker, hotel) {
      if (this.infoWindow) {
        // Get cached or generate InfoWindow content
        const content = this.getCachedInfoWindowContent(hotel)
        this.infoWindow.setContent(content)
        this.infoWindow.open(this.map, marker)

        // Center map on marker
        this.map.panTo(marker.getPosition())

        // Add click listeners to InfoWindow content after it's rendered
        this.$nextTick(() => {
          setTimeout(() => this.attachInfoWindowListeners(hotel), 100)
        })
      }
    },

    getCachedInfoWindowContent(hotel) {
      const cacheKey = hotel.uniqueKey

      // Check cache first
      if (this.infoWindowContentCache.has(cacheKey)) {
        // Move to end for LRU behavior
        const content = this.infoWindowContentCache.get(cacheKey)
        this.infoWindowContentCache.delete(cacheKey)
        this.infoWindowContentCache.set(cacheKey, content)
        return content
      }

      // Generate new content
      const content = this.generateOptimizedInfoWindowContent(hotel)

      // Implement LRU cache eviction
      if (this.infoWindowContentCache.size >= this.INFOWINDOW_CACHE_SIZE) {
        const firstKey = this.infoWindowContentCache.keys().next().value
        this.infoWindowContentCache.delete(firstKey)
      }

      this.infoWindowContentCache.set(cacheKey, content)
      return content
    },

    generateOptimizedInfoWindowContent(hotel) {
      // Get hotel data
      const hotelImage = this.getHotelImage(hotel)
      const pricing = this.getHotelPricing(hotel)
      const amenities = this.getHotelAmenities(hotel)
      const distanceText = `${hotel.coordinates.distanceFromSearchedLocation.toFixed(
        2
      )} mi away`

      // Generate amenities HTML
      const amenitiesHtml =
        amenities.length > 0
          ? `
        <div class="amenities-compact" style="display: flex; flex-wrap: wrap; gap: 4px;">
          ${amenities
            .map(
              (amenity) => `
            <div class="amenity-badge" style="display: flex; align-items: center; gap: 4px; border-radius: 10px; font-size: 11px; color: #333; white-space: nowrap; font-weight: 400;">
              <i class="${amenity.icon}" style="font-size: 11px; color: #333;"></i>
              <span>${amenity.label}</span>
            </div>
          `
            )
            .join("")}
        </div>
      `
          : ""

      // Use pre-compiled template for better performance
      const imageSection = this.infoWindowTemplate.imageSection(
        hotelImage,
        hotel.hotelName
      )
      const detailsSection = this.infoWindowTemplate.detailsSection(
        hotel.hotelName,
        distanceText,
        amenitiesHtml,
        pricing
      )

      return this.infoWindowTemplate.wrapper(`
        <div class="columns is-multiline">
          ${imageSection}
          ${detailsSection}
        </div>
      `)
    },

    fitMapBounds() {
      if (!this.hotels.length) return

      const bounds = new window.google.maps.LatLngBounds()

      // Include search location
      bounds.extend({
        lat: this.searchLocation.latitude,
        lng: this.searchLocation.longitude,
      })

      // Include all hotels (not just active markers)
      this.hotels.forEach((hotel) => {
        if (hotel.coordinates) {
          bounds.extend({
            lat: hotel.coordinates.latitude,
            lng: hotel.coordinates.longitude,
          })
        }
      })

      this.map.fitBounds(bounds, 0)
    },

    setupMapEventListeners() {
      if (!this.map) return

      // Store initial map bounds for comparison after a short delay
      // This ensures the map is fully loaded and positioned
      setTimeout(() => {
        if (!this.initialMapBounds && this.map) {
          this.initialMapBounds = this.map.getBounds()
          this.initialBoundsSet = true
          console.log("Initial map bounds set:", this.initialMapBounds)
        }
      }, 1000)

      // Listen for map movement start (dragstart, zoom_changed)
      this.map.addListener("dragstart", () => {
        this.onDragStart()
      })

      this.map.addListener("zoom_changed", () => {
        this.onZoomStart()
      })

      // Listen for map movement end (dragend, idle)
      this.map.addListener("dragend", () => {
        this.onDragEnd()
      })

      // Listen for idle event (when map stops moving/zooming)
      this.map.addListener("idle", () => {
        this.onMapIdle()
      })

      // Listen for bounds changed for more responsive updates
      this.map.addListener("bounds_changed", () => {
        this.debouncedBoundsChanged()
      })
    },

    onDragStart() {
      this.hideSearchAreaButton()
      this.cancelPendingUpdates()
    },

    onZoomStart() {
      this.hideSearchAreaButton()
      this.debouncedBoundsChanged()
    },

    onDragEnd() {
      this.debouncedMapMoveEnd()
    },

    onMapIdle() {
      this.debouncedIdleHandler()
    },

    // Debounced event handlers for better performance
    debouncedIdleHandler() {
      if (this.idleDebounceTimeout) {
        clearTimeout(this.idleDebounceTimeout)
      }

      this.idleDebounceTimeout = setTimeout(() => {
        this.debouncedUpdateMarkers()
        this.debouncedCalcHotelsInViewport()
        this.handleMapMoveEnd()
      }, this.IDLE_DEBOUNCE)
    },

    debouncedBoundsChanged() {
      if (this.boundsChangedTimeout) {
        clearTimeout(this.boundsChangedTimeout)
      }

      this.boundsChangedTimeout = setTimeout(() => {
        this.debouncedUpdateMarkers()
        this.debouncedCalcHotelsInViewport()
      }, this.BOUNDS_CHANGED_DEBOUNCE)
    },

    debouncedMapMoveEnd() {
      // Use existing handleMapMoveEnd but ensure it's not called too frequently
      this.handleMapMoveEnd()
    },

    debouncedCalcHotelsInViewport() {
      if (this.viewportCalculationTimeout) {
        clearTimeout(this.viewportCalculationTimeout)
      }

      this.viewportCalculationTimeout = setTimeout(() => {
        this.calcHotelsInViewport()
      }, this.VIEWPORT_CALC_DEBOUNCE)
    },

    cancelPendingUpdates() {
      // Cancel all pending debounced operations
      if (this.idleDebounceTimeout) {
        clearTimeout(this.idleDebounceTimeout)
        this.idleDebounceTimeout = null
      }

      if (this.boundsChangedTimeout) {
        clearTimeout(this.boundsChangedTimeout)
        this.boundsChangedTimeout = null
      }

      if (this.viewportCalculationTimeout) {
        clearTimeout(this.viewportCalculationTimeout)
        this.viewportCalculationTimeout = null
      }
    },

    calcHotelsInViewport() {
      if (!this.map) {
        this.hotelsInViewport = 0
        return
      }

      const bounds = this.map.getBounds()
      if (!bounds) {
        this.hotelsInViewport = 0
        return
      }

      // Optimized viewport calculation using active markers when possible
      if (
        this.activeMarkers.size > 0 &&
        this.activeMarkers.size < this.hotels.length
      ) {
        // Use active markers for faster calculation when we have a subset
        let count = 0
        for (const marker of this.activeMarkers.values()) {
          const position = marker.getPosition()
          if (position && bounds.contains(position)) {
            count++
          }
        }
        this.hotelsInViewport = count
      } else {
        // Fall back to checking all hotels
        let count = 0
        this.hotels.forEach((hotel) => {
          if (hotel.coordinates) {
            const position = new window.google.maps.LatLng(
              hotel.coordinates.latitude,
              hotel.coordinates.longitude
            )
            if (bounds.contains(position)) {
              count++
            }
          }
        })
        this.hotelsInViewport = count
      }
    },

    getHotelImage(hotel) {
      if (
        hotel.description &&
        hotel.description.imagesAndDescriptions &&
        hotel.description.imagesAndDescriptions.hotel &&
        hotel.description.imagesAndDescriptions.hotel.images &&
        hotel.description.imagesAndDescriptions.hotel.images.length > 0
      ) {
        return hotel.description.imagesAndDescriptions.hotel.images[0].url
      }
      return "https://ganttravel.tripcircuit.com/images/defaultHotel.jpg"
    },

    getHotelPricing(hotel) {
      const room = hotel.rooms && hotel.rooms[0]
      if (!room) return { dailyRate: 0, total: 0 }

      const pricing = room.requestedCurrencyPricing || {}
      return {
        dailyRate: pricing.dailyRate || 0,
        total: pricing.total || 0,
        originalRate: this.getOriginalRate(room, hotel),
        currencyCode: hotel.currencyCode,
        currencySymbol: this.$helpers.getCurrencySymbol(hotel.currencyCode),
      }
    },

    getOriginalRate(room, hotel) {
      if (room.requestedCurrencyPricing) {
        const originalDaily = Math.round(
          room.requestedCurrencyPricing.amountAfterTax / hotel.tripLength
        )
        const currentDaily = room.requestedCurrencyPricing.dailyRate
        return originalDaily > currentDaily ? originalDaily : null
      }
      return null
    },

    getHotelAmenities(hotel) {
      if (!this.$store.state.hotelSearch.filters.amenities.options) return []

      return this.$store.state.hotelSearch.filters.amenities.options.filter(
        (amenityOption) => {
          return hotel.description?.amenities?.includes(amenityOption.value)
        }
      )
    },

    attachInfoWindowListeners(hotel) {
      // Add click listeners to InfoWindow elements
      const hotelNameElement =
        document.querySelector(".gm-style-iw-c .hotel-name") ||
        document.querySelector(".gm-style-iw .hotel-name")
      const hotelImageElement =
        document.querySelector(".gm-style-iw-c .hotel-image") ||
        document.querySelector(".gm-style-iw .hotel-image")
      const viewButtonElement =
        document.querySelector(".gm-style-iw-c .view-button") ||
        document.querySelector(".gm-style-iw .view-button")

      const clickHandler = () => this.gotoHotelDetails(hotel)

      if (hotelNameElement) {
        hotelNameElement.addEventListener("click", clickHandler)
        hotelNameElement.style.cursor = "pointer"
      }

      if (hotelImageElement) {
        hotelImageElement.addEventListener("click", clickHandler)
        hotelImageElement.style.cursor = "pointer"
      }

      if (viewButtonElement) {
        viewButtonElement.addEventListener("click", clickHandler)
        viewButtonElement.style.cursor = "pointer"
      }
    },

    gotoHotelDetails(hotel) {
      // Only save minimal hotel data needed for display before live search completes
      const minimalHotelData = {
        hotelCode: hotel.hotelCode,
        hotelName: hotel.hotelName,
        chainName: hotel.chainName,
        address: {
          AddressLine: hotel.address?.AddressLine,
          CityName: hotel.address?.CityName,
          State: hotel.address?.State,
          PostalCode: hotel.address?.PostalCode
        },
        geocode: {
          Latitude: hotel.geocode?.Latitude,
          Longitude: hotel.geocode?.Longitude
        }
      }

      this.$store.dispatch("hotelSearch/saveHotel", minimalHotelData)

      const cacheKey = this.$route.query.key || null

      if (this.$store.getters["meetingsAndTrips/isMeeting"]) {
        this.$nav.pushRouteTo({
          name: "MeetingHotelDetail",
          params: { hotelCode: hotel.hotelCode },
          query: { key: cacheKey },
        })
      } else {
        this.$nav.pushRouteTo({
          name: "hotel-detail",
          params: { hotelCode: hotel.hotelCode, key: cacheKey },
          query: { key: cacheKey },
        })
      }
    },

    // Search by map functionality methods
    handleMapMoveEnd() {
      // Clear any existing timeouts
      if (this.searchAreaButtonTimeout) {
        clearTimeout(this.searchAreaButtonTimeout)
      }
      if (this.searchAreaAutoHideTimeout) {
        clearTimeout(this.searchAreaAutoHideTimeout)
      }

      // Check if map has moved significantly from initial position
      if (this.hasMapMovedSignificantly()) {
        console.log("Map moved significantly")

        // Show button after 300ms delay
        this.searchAreaButtonTimeout = setTimeout(() => {
          if (!this.isSearching) {
            this.showSearchAreaButton = true
          }
        }, 300)
      }
    },

    hideSearchAreaButton() {
      this.showSearchAreaButton = false
      if (this.searchAreaButtonTimeout) {
        clearTimeout(this.searchAreaButtonTimeout)
        this.searchAreaButtonTimeout = null
      }
      if (this.searchAreaAutoHideTimeout) {
        clearTimeout(this.searchAreaAutoHideTimeout)
        this.searchAreaAutoHideTimeout = null
      }
    },

    hasMapMovedSignificantly() {
      // Return false if initial bounds haven't been properly set yet
      if (!this.initialMapBounds || !this.map || !this.initialBoundsSet) {
        console.log(
          "Initial bounds not ready yet, map hasn't moved significantly"
        )
        return false
      }

      const currentBounds = this.map.getBounds()
      if (!currentBounds) {
        console.log("No current bounds available")
        return false
      }

      const initialCenter = this.initialMapBounds.getCenter()
      const currentCenter = currentBounds.getCenter()

      // Calculate distance between centers (in meters)
      const distance = this.calculateDistance(
        initialCenter.lat(),
        initialCenter.lng(),
        currentCenter.lat(),
        currentCenter.lng()
      )

      // Get current zoom and compare with initial zoom
      const currentZoom = this.map.getZoom()
      const initialZoom = 16 // Initial zoom level from map initialization
      const zoomDifference = Math.abs(currentZoom - initialZoom)

      // Calculate bounds overlap
      const boundsOverlap = this.calculateBoundsOverlap(
        this.initialMapBounds,
        currentBounds
      )

      // Log the comparison values for debugging
      console.log("Map movement check:", {
        distance: Math.round(distance),
        zoomDifference: zoomDifference.toFixed(1),
        boundsOverlap: boundsOverlap.toFixed(2),
        currentZoom,
        initialZoom,
      })

      // Consider significant if moved more than 500 meters or zoom changed by more than 1 level
      // Also check if the current bounds don't significantly overlap with initial bounds
      const hasMovedSignificantly =
        distance > 500 || zoomDifference > 1 || boundsOverlap < 0.7

      if (hasMovedSignificantly) {
        console.log("Map has moved significantly!")
      }

      return hasMovedSignificantly
    },

    calculateBoundsOverlap(bounds1, bounds2) {
      // Calculate the overlap percentage between two bounds
      const ne1 = bounds1.getNorthEast()
      const sw1 = bounds1.getSouthWest()
      const ne2 = bounds2.getNorthEast()
      const sw2 = bounds2.getSouthWest()

      // Calculate intersection bounds
      const intersectionNE = {
        lat: Math.min(ne1.lat(), ne2.lat()),
        lng: Math.min(ne1.lng(), ne2.lng()),
      }
      const intersectionSW = {
        lat: Math.max(sw1.lat(), sw2.lat()),
        lng: Math.max(sw1.lng(), sw2.lng()),
      }

      // Check if there's any intersection
      if (
        intersectionNE.lat <= intersectionSW.lat ||
        intersectionNE.lng <= intersectionSW.lng
      ) {
        return 0 // No overlap
      }

      // Calculate areas
      const intersectionArea =
        (intersectionNE.lat - intersectionSW.lat) *
        (intersectionNE.lng - intersectionSW.lng)
      const bounds1Area = (ne1.lat() - sw1.lat()) * (ne1.lng() - sw1.lng())
      const bounds2Area = (ne2.lat() - sw2.lat()) * (ne2.lng() - sw2.lng())

      // Return overlap as percentage of the smaller bounds
      const smallerArea = Math.min(bounds1Area, bounds2Area)
      return intersectionArea / smallerArea
    },

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371e3 // Earth's radius in meters
      const φ1 = (lat1 * Math.PI) / 180
      const φ2 = (lat2 * Math.PI) / 180
      const Δφ = ((lat2 - lat1) * Math.PI) / 180
      const Δλ = ((lng2 - lng1) * Math.PI) / 180

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      return R * c
    },

    calculateMapEdgeRadius(bounds, center) {
      // Calculate radius from center to approximate edge of the visible map area
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      // Calculate distances from center to each corner
      const distanceToNE = this.calculateDistance(
        center.lat(), center.lng(),
        ne.lat(), ne.lng()
      )

      const distanceToSW = this.calculateDistance(
        center.lat(), center.lng(),
        sw.lat(), sw.lng()
      )

      const distanceToNW = this.calculateDistance(
        center.lat(), center.lng(),
        ne.lat(), sw.lng()
      )

      const distanceToSE = this.calculateDistance(
        center.lat(), center.lng(),
        sw.lat(), ne.lng()
      )

      // Use the average of the corner distances, but bias toward the shorter distances
      // to ensure we capture most of the visible area without going too far beyond
      const distances = [distanceToNE, distanceToSW, distanceToNW, distanceToSE]
      const avgDistance = distances.reduce((sum, dist) => sum + dist, 0) / distances.length

      // Use 75% of average distance to ensure good coverage without excessive radius
      const radiusInMeters = avgDistance * 0.75

      // Convert to miles
      const radiusInMiles = radiusInMeters / 1609.34

      return radiusInMiles
    },

    handleRadiusEdgeCases(radiusInMiles, bounds) {
      // Handle special cases for map boundaries and API limits

      // Check if we're near poles (latitude > 85 or < -85)
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const maxLat = Math.max(Math.abs(ne.lat()), Math.abs(sw.lat()))

      if (maxLat > 85) {
        // Near poles - distances are distorted, use conservative radius
        return Math.min(radiusInMiles, 15)
      }

      // Check if bounds cross the international date line (longitude wraps around)
      const lngSpan = ne.lng() - sw.lng()
      if (lngSpan < 0) {
        // Crossing date line - adjust radius calculation
        const correctedSpan = 360 + lngSpan
        if (correctedSpan > 180) {
          // Very wide area crossing date line, use larger radius
          return Math.min(radiusInMiles * 1.2, 50)
        }
      }

      // API limits: minimum 1 mile, maximum 50 miles
      const clampedRadius = Math.max(1, Math.min(radiusInMiles, 50))

      // For very zoomed-in areas, ensure minimum useful search radius
      if (clampedRadius < 2) {
        return 2
      }

      // For very zoomed-out areas, ensure we don't exceed practical limits
      if (clampedRadius > 40) {
        return 40
      }

      return clampedRadius
    },

    async searchThisArea() {
      if (!this.map || this.isSearching) return

      try {
        this.isSearching = true
        this.hideSearchAreaButton()
        this.disableMapInteraction()

        // Get current map bounds and center
        const bounds = this.map.getBounds()
        const center = bounds.getCenter()

        // Calculate dynamic radius from center to approximate edge of map
        const radiusInMiles = this.calculateMapEdgeRadius(bounds, center)

        // Handle edge cases for very small or very large areas
        const finalRadius = this.handleRadiusEdgeCases(radiusInMiles, bounds)

        // Get user-friendly location name using reverse geocoding
        const locationName = await this.getLocationNameFromCoordinates(
          center.lat(),
          center.lng()
        )

        // Create new search location object
        const newSearchLocation = {
          name: locationName,
          latitude: center.lat(),
          longitude: center.lng(),
          radius: finalRadius,
        }

        // Update search location
        //this.searchLocation = newSearchLocation

        // Trigger new search via store action
        const result = await this.$store.dispatch(
          "hotelSearch/searchByMapArea",
          {
            location: newSearchLocation,
            searchDates: this.$store.state.hotelSearch.searchDates,
          }
        )

        // Update initial bounds to current position after successful search
        // This ensures hasMapMovedSignificantly returns false for the new search results
        this.initialMapBounds = bounds
        this.initialBoundsSet = true
        console.log("Updated initial bounds after search:", bounds)

        // Update search center marker to show new search location
        this.updateSearchCenterMarker({
          ...newSearchLocation,
          radius: finalRadius
        })

        // Show success message with result count
        if (result.data && result.data.length > 0) {
          this.$buefy.toast.open({
            message: `Found ${result.data.length} hotels in ${locationName}`,
            type: "is-success",
            duration: 2000,
          })

          // Log the search event
          this.$app.logEvent({
            event_type: "Map Area Search",
            search_location: newSearchLocation.name,
            results_count: result.data.length,
            radius_miles: finalRadius,
          })
        } else {
          this.$buefy.toast.open({
            message: `No hotels found in ${locationName}. Try expanding the search area.`,
            type: "is-warning",
            duration: 3000,
          })
        }
      } catch (error) {
        console.error("Error searching this area:", error)

        // Show specific error messages based on error type
        let errorMessage = "Failed to search this area. Please try again."

        if (error.message && error.message.includes("timeout")) {
          errorMessage =
            "Search timed out. Please try again or zoom in to a smaller area."
        } else if (error.message && error.message.includes("network")) {
          errorMessage =
            "Network error. Please check your connection and try again."
        } else if (this.$store.state.hotelSearch.searchErrors.length > 0) {
          const firstError = this.$store.state.hotelSearch.searchErrors[0]
          errorMessage = firstError.title || errorMessage
        }

        this.$buefy.toast.open({
          message: errorMessage,
          type: "is-danger",
          duration: 4000,
        })
      } finally {
        this.isSearching = false
        this.enableMapInteraction()
      }
    },

    disableMapInteraction() {
      if (!this.map) return

      this.map.setOptions({
        draggable: false,
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
      })
    },

    enableMapInteraction() {
      if (!this.map) return

      this.map.setOptions({
        draggable: true,
        zoomControl: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
      })
    },

    // Memory Management Methods
    startMemoryManagement() {
      // Set up periodic memory cleanup
      this.memoryCleanupInterval = setInterval(() => {
        this.performMemoryCleanup()
      }, this.MEMORY_CLEANUP_INTERVAL)
    },

    performMemoryCleanup() {
      // Clean up marker pool if it's too large
      if (this.markerPool.length > this.MARKER_POOL_SIZE) {
        const excessMarkers = this.markerPool.splice(this.MARKER_POOL_SIZE)
        excessMarkers.forEach((marker) => {
          if (marker._clickListener) {
            window.google.maps.event.removeListener(marker._clickListener)
          }
          marker.setMap(null)
        })
      }

      // Clean up icon cache if it's too large
      if (this.iconCache.size > this.ICON_CACHE_SIZE) {
        const keysToDelete = Array.from(this.iconCache.keys()).slice(
          0,
          this.iconCache.size - this.ICON_CACHE_SIZE
        )
        keysToDelete.forEach((key) => this.iconCache.delete(key))
      }

      // Clean up InfoWindow content cache if it's too large
      if (this.infoWindowContentCache.size > this.INFOWINDOW_CACHE_SIZE) {
        const keysToDelete = Array.from(
          this.infoWindowContentCache.keys()
        ).slice(
          0,
          this.infoWindowContentCache.size - this.INFOWINDOW_CACHE_SIZE
        )
        keysToDelete.forEach((key) => this.infoWindowContentCache.delete(key))
      }

      // Force garbage collection hint (if available)
      if (window.gc && typeof window.gc === "function") {
        try {
          window.gc()
        } catch (e) {
          // Ignore errors - gc() might not be available
        }
      }

      console.log("Memory cleanup performed:", {
        activeMarkers: this.activeMarkers.size,
        markerPool: this.markerPool.length,
        iconCache: this.iconCache.size,
        infoWindowCache: this.infoWindowContentCache.size,
      })
    },

    stopMemoryManagement() {
      if (this.memoryCleanupInterval) {
        clearInterval(this.memoryCleanupInterval)
        this.memoryCleanupInterval = null
      }
    },

    // Debug method to manually test clustering
    debugClustering() {
      console.log("=== Clustering Debug Info ===")
      console.log("Map:", this.map)
      console.log("Hotel markers refs:", this.$refs.hotelMarkers)

      if (this.$refs.hotelMarkers) {
        const markers = this.$refs.hotelMarkers
          .map((markerComponent, index) => {
            const marker = markerComponent.getMarker()
            console.log(
              `Marker ${index}:`,
              marker,
              markerComponent.hotel?.hotelName
            )
            return marker
          })
          .filter((marker) => marker !== null)

        console.log("Valid markers:", markers.length)
        console.log("MarkerClusterer instance:", this.markerClusterer)

        // Force re-setup clustering
        this.setupMarkerClustering()
      }
    },

    cleanup() {
      // Clear timeouts
      if (this.viewportUpdateTimeout) {
        clearTimeout(this.viewportUpdateTimeout)
        this.viewportUpdateTimeout = null
      }

      if (this.mapMoveTimeout) {
        clearTimeout(this.mapMoveTimeout)
        this.mapMoveTimeout = null
      }

      if (this.searchAreaButtonTimeout) {
        clearTimeout(this.searchAreaButtonTimeout)
        this.searchAreaButtonTimeout = null
      }

      if (this.markerUpdateDebounceTimeout) {
        clearTimeout(this.markerUpdateDebounceTimeout)
        this.markerUpdateDebounceTimeout = null
      }

      if (this.clusterUpdateTimeout) {
        clearTimeout(this.clusterUpdateTimeout)
        this.clusterUpdateTimeout = null
      }

      // Cancel all debounced operations
      this.cancelPendingUpdates()

      // Cancel progressive loading
      this.cancelProgressiveLoading()

      // Stop memory management
      this.stopMemoryManagement()

      // Close InfoWindow
      if (this.infoWindow) {
        this.infoWindow.close()
        this.infoWindow = null
      }

      // Clear all active markers
      for (const marker of this.activeMarkers.values()) {
        if (marker._clickListener) {
          window.google.maps.event.removeListener(marker._clickListener)
        }
        marker.setMap(null)
      }
      this.activeMarkers.clear()

      // Clear marker pool
      this.markerPool.forEach((marker) => {
        if (marker._clickListener) {
          window.google.maps.event.removeListener(marker._clickListener)
        }
        marker.setMap(null)
      })
      this.markerPool = []

      // Clear clusterer
      if (this.markerClusterer) {
        try {
          this.markerClusterer.clearMarkers()
          this.markerClusterer.setMap(null)
        } catch (error) {
          console.warn("Error clearing marker clusterer:", error)
        }
        this.markerClusterer = null
      }

      // Clear caches
      this.iconCache.clear()
      this.locationNameCache.clear()
      this.infoWindowContentCache.clear()
      this.clusterCache.clear()

      // Reset viewport count
      this.hotelsInViewport = 0

      // Reset initial bounds tracking
      this.initialMapBounds = null
      this.initialBoundsSet = false
      this.currentViewportBounds = null

      // Remove search center marker
      this.removeSearchCenterMarker()

      // Clear map reference
      this.map = null
    },
  },

  beforeDestroy() {
    this.cleanup()
    // Ensure popstate listener is removed
    window.removeEventListener("popstate", this.handleBackButton)
  },
}
</script>

<style scoped>
/* Modal container */
.modal-container {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Top panel - white background with black text */
.top-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  color: #333333;
  padding: 0.8rem 1.5rem;
  min-height: 50px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #e0e0e0;
  z-index: 1000;
  flex-shrink: 0;
}

/* Back button */
.back-button-container {
  flex-shrink: 0;
  margin-right: 16px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.back-button:hover {
  background: #f5f5f5;
  border-color: #ccc;
  color: #000;
}

.back-button:focus {
  outline: 2px solid #2c5aa0;
  outline-offset: 2px;
}

.back-button i {
  font-size: 12px;
}

/* Search location info */
.search-location-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.search-location-label {
  font-size: 0.85rem;
  color: #444444;
}

.search-location-title {
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #333333;
}

/* Close button - styled for contrast against white background */
.close-button {
  background-color: #f8f9fa;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  transform: scale(1.05);
}

.close-button:before,
.close-button:after {
  background-color: #212529 !important;
}

/* Full-screen map */
.full-screen-map {
  width: 100%;
  flex: 1;
  border: none;
  border-radius: 0;
}

/* Search This Area Button */
.search-area-button-container {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: auto;
}

.search-area-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #4285f4;
  border-radius: 24px;
  padding: 12px 20px;
  color: #4285f4;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-family: inherit;
}

.search-area-button:hover {
  background: #f8f9fa;
  border-color: #42aaf4;
  color: #42aaf4;
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.2);
}

.search-area-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-area-button i {
  font-size: 14px;
}

/* Search Loading Overlay */
.search-loading-overlay {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  pointer-events: none;
}

.search-loading-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 12px 20px;
  border-radius: 24px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Progressive Loading Overlay */
.marker-loading-overlay {
  position: absolute;
  top: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  pointer-events: none;
}

.marker-loading-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: rgba(66, 133, 244, 0.9);
  padding: 10px 16px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Info overlay positioned in bottom-left corner */
.map-info-overlay {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
  pointer-events: none;
}

.info-card {
  background-color: rgba(0, 0, 0, 0.6);
  padding: 12px 16px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Responsive adjustments */
@media screen and (max-width: 600px) {
  /* Back button responsive adjustments */
  .back-button-container {
    margin-right: 8px;
  }

  .back-button {
    padding: 6px 8px;
    font-size: 0.8rem;
  }

  .back-button span {
    display: none; /* Hide text on small screens, keep icon */
  }

  .back-button i {
    font-size: 14px;
  }

  .search-location-title {
    font-size: 1.125rem;
  }

  .search-location-subtitle {
    font-size: 0.8rem;
  }

  /* Search button responsive adjustments */
  .search-area-button {
    padding: 10px 16px;
    font-size: 0.8rem;
  }

  .search-area-button span {
    display: none; /* Hide text on small screens, keep icon */
  }

  .search-area-button i {
    font-size: 16px;
  }

  .search-loading-card {
    padding: 10px 16px;
    font-size: 0.8rem;
  }
}

@media screen and (max-width: 450px) {
  .back-button {
    padding: 4px 6px;
  }

  .back-button i {
    font-size: 16px;
  }

  .search-location-title {
    font-size: 1rem;
  }
}

/* Override Buefy modal styles for full-screen experience */
:deep(.modal-card) {
  margin: 0 !important;
  max-height: none !important;
  height: 100vh !important;
  width: 100vw !important;
  border-radius: 0 !important;
  overflow: hidden !important;
}

:deep(.modal-background) {
  background-color: rgba(0, 0, 0, 0.8);
}

:deep(.modal) {
  overflow: hidden !important;
}

/* Disabled state styles */
.grayscale {
  filter: grayscale(100%);
  transition: filter 0.2s ease;
}

.filter-count-badge {
  background-color: #3273dc;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 1.5rem;
  text-align: center;
  line-height: 1;
}
</style>
